<div class="pricing">
  <header class="pricing-nav">
    <a class="pricing-nav__brand" [routerLink]="mktPath.home()" [attr.aria-label]="brandWordmark">
      <img class="pricing-nav__logo" [src]="brandLogoSrc" [alt]="brandWordmark" />
    </a>
    <div class="pricing-nav__actions">
      <a class="pricing-btn pricing-btn--ghost" [href]="loginUrl">{{
        'website.nav.login' | translate
      }}</a>
      <a class="pricing-btn pricing-btn--lime" [href]="registerUrl">{{
        'website.cta.trial' | translate
      }}</a>
    </div>
  </header>

  <main class="pricing-main">
    <header class="pricing-hero">
      <p class="pricing-hero__eyebrow">{{ brandWordmark }}</p>
      <h1>{{ 'website.pricing.title' | translate }}</h1>
      <p>{{ 'website.pricing.sub' | translate }}</p>

      <ul class="pricing-trust" [attr.aria-label]="'website.pricing.trustAria' | translate">
        @for (key of trustKeys; track key) {
          <li>{{ 'website.pricing.trust.' + key | translate }}</li>
        }
      </ul>

      <a class="pricing-compare-jump" href="#comparar" (click)="scrollToCompare($event)">{{
        'website.pricing.compareJump' | translate
      }}</a>
    </header>

    <section class="pricing-includes" aria-labelledby="includes-title">
      <h2 id="includes-title" class="pricing-includes__title">
        {{ 'website.pricing.includes.title' | translate }}
      </h2>
      <ul class="pricing-includes__list">
        @for (key of includeKeys; track key) {
          <li>{{ 'website.pricing.includes.' + key | translate }}</li>
        }
      </ul>
    </section>

    <div class="pricing-carousel">
      <div class="pricing-carousel__toolbar">
        <p class="pricing-carousel__hint">{{ 'website.pricing.carouselHint' | translate }}</p>
        <div class="pricing-carousel__nav" role="group" [attr.aria-label]="'website.pricing.carouselNav' | translate">
          <button
            type="button"
            class="pricing-carousel__arrow"
            [disabled]="!canScrollPrev()"
            [attr.aria-label]="'website.pricing.carouselPrev' | translate"
            (click)="scrollPlans(-1)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M15 6 9 12l6 6"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            class="pricing-carousel__arrow"
            [disabled]="!canScrollNext()"
            [attr.aria-label]="'website.pricing.carouselNext' | translate"
            (click)="scrollPlans(1)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="m9 6 6 6-6 6"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div class="pricing-cards" #plansTrack tabindex="0" [attr.aria-label]="'website.pricing.carouselNav' | translate">
        @for (plan of plans(); track plan.code) {
          <article class="pricing-card" [class.pricing-card--hot]="plan.featured">
            <div class="pricing-card__top">
              <h2>{{ plan.marketingName }}</h2>
              @if (plan.featured) {
                <span class="pricing-card__badge">{{ 'website.plans.recommended' | translate }}</span>
              }
            </div>
            <p class="pricing-card__pitch">{{ 'website.plans.' + plan.code + '.pitch' | translate }}</p>
            <div class="pricing-card__price-block">
              <p class="pricing-card__price">{{ planPrice(plan) }}</p>
              @if (plan.trialDays > 0) {
                <p class="pricing-card__trial">{{
                  'website.plans.trialDays' | translate: { days: plan.trialDays }
                }}</p>
              }
              <p class="pricing-card__note">{{ 'website.plans.' + plan.code + '.priceNote' | translate }}</p>
            </div>
            @if (plan.salesLed) {
              <a class="pricing-btn pricing-btn--outline pricing-btn--block" [routerLink]="mktPath.home()" fragment="contacto">{{
                'website.plans.enterpriseCta' | translate
              }}</a>
            } @else {
              <a
                class="pricing-btn pricing-btn--solid pricing-btn--block"
                [href]="registerUrl"
                (click)="choosePlan(plan, $event)"
              >{{
                'website.cta.choosePlan' | translate
              }}</a>
            }
            <ul class="pricing-card__features">
              @for (feat of planFeatureKeys(plan.code); track feat) {
                <li>{{ 'website.plans.' + plan.code + '.features.' + feat | translate }}</li>
              }
            </ul>
          </article>
        }
      </div>
    </div>

    <section class="pricing-extras" aria-labelledby="extras-title">
      <div>
        <h2 id="extras-title">{{ 'website.pricing.extras.title' | translate }}</h2>
        <p>{{ 'website.pricing.extras.sub' | translate }}</p>
      </div>
      <div class="pricing-table-wrap">
        <table class="pricing-table">
          <thead>
            <tr>
              <th scope="col">{{ 'website.pricing.extras.planCol' | translate }}</th>
              <th scope="col">{{ 'website.pricing.extras.includedLocationCol' | translate }}</th>
              <th scope="col">{{ 'website.pricing.extras.userCol' | translate }}</th>
              <th scope="col">{{ 'website.pricing.extras.locationCol' | translate }}</th>
              <th scope="col">{{ 'website.pricing.extras.noteCol' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @for (id of extraRows(); track id) {
              <tr>
                <th scope="row">{{ 'website.plans.' + id + '.name' | translate }}</th>
                <td>{{ 'website.pricing.extras.rows.' + id + '.includedLocation' | translate }}</td>
                <td>{{ 'website.pricing.extras.rows.' + id + '.user' | translate }}</td>
                <td>{{ 'website.pricing.extras.rows.' + id + '.location' | translate }}</td>
                <td>{{ 'website.pricing.extras.rows.' + id + '.note' | translate }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>

    <section id="comparar" class="pricing-compare" aria-labelledby="compare-title">
      <h2 id="compare-title">{{ 'website.pricing.compareTitle' | translate }}</h2>
      <p class="pricing-compare__hint">{{ 'website.pricing.compareHint' | translate }}</p>

      <div class="pricing-table-wrap">
        <table class="pricing-table">
          <thead>
            <tr>
              <th scope="col">{{ 'website.pricing.featureCol' | translate }}</th>
              @for (plan of plans(); track plan.code) {
                <th scope="col">{{ plan.marketingName }}</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of compareRows; track row) {
              <tr>
                <th scope="row">{{ 'website.pricing.compare.' + row + '.label' | translate }}</th>
                @for (id of planIds(); track id) {
                  <td>{{ cellKey(id, row) | translate }}</td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>

    <section id="faq-precios" class="pricing-faq" aria-labelledby="pricing-faq-title">
      <div class="pricing-faq__intro">
        <h2 id="pricing-faq-title">{{ 'website.pricing.faq.title' | translate }}</h2>
        <p>{{ 'website.pricing.faq.sub' | translate }}</p>
      </div>
      <div class="pricing-faq__list">
        @for (key of faqKeys; track key) {
          <details class="pricing-faq__item">
            <summary>{{ 'website.pricing.faq.' + key + '.q' | translate }}</summary>
            <p>{{ 'website.pricing.faq.' + key + '.a' | translate: { days: paidTrialDays() } }}</p>
          </details>
        }
      </div>
    </section>

    <app-website-media-slot
      class="pricing-slot"
      variant="wide"
      [label]="'website.placeholders.pricingVisual.label' | translate"
      [hint]="'website.placeholders.pricingVisual.hint' | translate"
    />

    <section class="pricing-final">
      <h2>{{ 'website.final.title' | translate }}</h2>
      <a class="pricing-btn pricing-btn--lime pricing-btn--lg" [href]="registerUrl">{{
        'website.cta.trial' | translate
      }}</a>
      <a class="pricing-back" [routerLink]="mktPath.home()">{{ 'website.pricing.backHome' | translate }}</a>
    </section>
  </main>
</div>
